// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Upload, Trash2, Edit2, Check, X, DollarSign, GraduationCap, Eye, EyeOff } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface EventCoach {
  id?: string;
  coach_name: string;
  college_id: string | null;
  college_name?: string | null;
  division: string;
  logo_url: string | null;
  external_url: string | null;
  coach_email?: string | null;
  coach_phone?: string | null;
  payment_status?: string | null;
  hide_from_public?: boolean;
  metadata?: {
    additional_coaches?: string[];
    additional_coach_qty?: number;
    hospitality_days?: string[];
    filler_name?: string;
    is_ncaa_live?: boolean;
    coupon_applied?: string;
    discount_amount?: number;
  };
}

interface CoachesTabProps {
  eventId: string;
}

const CoachesTab: React.FC<CoachesTabProps> = ({ eventId }) => {
  const [coaches, setCoaches] = useState<EventCoach[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [coachesExternalUrl, setCoachesExternalUrl] = useState<string>('');
  const [coachAttendanceFee, setCoachAttendanceFee] = useState<string>('0');
  const [coachCouponCode, setCoachCouponCode] = useState<string>('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  // NCAA Live event fields
  const [isNcaaLiveEvent, setIsNcaaLiveEvent] = useState(false);
  const [ncaaD1FirstFee, setNcaaD1FirstFee] = useState<string>('395');
  const [ncaaD1AdditionalFee, setNcaaD1AdditionalFee] = useState<string>('100');
  const [ncaaOtherFirstFee, setNcaaOtherFirstFee] = useState<string>('125');
  const [ncaaOtherAdditionalFee, setNcaaOtherAdditionalFee] = useState<string>('50');
  const [ncaaHospitalityDays, setNcaaHospitalityDays] = useState<string>('');
  
  // Multiple coupon codes support
  interface CouponCode {
    id?: string;
    code: string;
    discount_type: 'percentage' | 'fixed';
    discount_value: string;
    is_active: boolean;
  }
  const [couponCodes, setCouponCodes] = useState<CouponCode[]>([]);
  
  // Coach registration template
  interface CommunicationTemplate {
    id: string;
    name: string;
    type: string;
    category: string;
  }
  const [registrationTemplateId, setRegistrationTemplateId] = useState<string | null>(null);
  const [availableTemplates, setAvailableTemplates] = useState<CommunicationTemplate[]>([]);
  const [coachRegistrationInstructions, setCoachRegistrationInstructions] = useState<string>('');
  const [coachNotificationEmail, setCoachNotificationEmail] = useState<string>('');

  const divisions = ['D1', 'D2', 'D3', 'JUCO', 'NAIA', 'Other'];

  useEffect(() => {
    fetchEventCoaches();
    fetchEventData();
    fetchCouponCodes();
    fetchTemplates();
  }, [eventId]);

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('communication_templates')
        .select('id, name, type, category')
        .eq('is_active', true)
        .in('category', ['event-emails', 'coach', 'event'])
        .order('name');

      if (error) throw error;
      setAvailableTemplates(data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchCouponCodes = async () => {
    try {
      const { data, error } = await supabase
        .from('event_coupon_codes')
        .select('*')
        .eq('event_id', eventId)
        .order('created_at');

      if (error) throw error;
      
      setCouponCodes((data || []).map(c => ({
        id: c.id,
        code: c.code,
        discount_type: c.discount_type as 'percentage' | 'fixed',
        discount_value: c.discount_type === 'fixed' 
          ? String(Number(c.discount_value) / 100) 
          : String(c.discount_value),
        is_active: c.is_active
      })));
    } catch (error) {
      console.error('Error fetching coupon codes:', error);
    }
  };

  const fetchEventData = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('coaches_external_url, coach_attendance_fee, coach_coupon_code, is_ncaa_live_event, ncaa_d1_first_coach_fee, ncaa_d1_additional_coach_fee, ncaa_other_first_coach_fee, ncaa_other_additional_coach_fee, ncaa_hospitality_days, coach_registration_template_id, coach_registration_instructions, coach_notification_email')
        .eq('id', eventId)
        .maybeSingle();

      if (error) throw error;
      
      console.log('🔄 Fetched event coach settings:', data);
      setCoachesExternalUrl(data?.coaches_external_url || '');
      setCoachAttendanceFee(data?.coach_attendance_fee ? String(data.coach_attendance_fee / 100) : '0');
      setCoachCouponCode(data?.coach_coupon_code || '');
      
      // NCAA Live fields - use null checks instead of falsy checks to preserve 0 values
      setIsNcaaLiveEvent(data?.is_ncaa_live_event || false);
      setNcaaD1FirstFee(data?.ncaa_d1_first_coach_fee !== null && data?.ncaa_d1_first_coach_fee !== undefined 
        ? String(data.ncaa_d1_first_coach_fee / 100) : '395');
      setNcaaD1AdditionalFee(data?.ncaa_d1_additional_coach_fee !== null && data?.ncaa_d1_additional_coach_fee !== undefined 
        ? String(data.ncaa_d1_additional_coach_fee / 100) : '100');
      setNcaaOtherFirstFee(data?.ncaa_other_first_coach_fee !== null && data?.ncaa_other_first_coach_fee !== undefined 
        ? String(data.ncaa_other_first_coach_fee / 100) : '125');
      setNcaaOtherAdditionalFee(data?.ncaa_other_additional_coach_fee !== null && data?.ncaa_other_additional_coach_fee !== undefined 
        ? String(data.ncaa_other_additional_coach_fee / 100) : '50');
      setNcaaHospitalityDays((data?.ncaa_hospitality_days || []).join('\n'));
      setRegistrationTemplateId(data?.coach_registration_template_id || null);
      setCoachRegistrationInstructions(data?.coach_registration_instructions || '');
      setCoachNotificationEmail(data?.coach_notification_email || '');
      
      console.log('🔄 NCAA Live fields loaded:', {
        is_ncaa_live_event: data?.is_ncaa_live_event,
        ncaa_d1_first_coach_fee: data?.ncaa_d1_first_coach_fee,
        ncaa_hospitality_days: data?.ncaa_hospitality_days,
        coach_registration_template_id: data?.coach_registration_template_id,
        coach_registration_instructions: data?.coach_registration_instructions
      });
    } catch (error) {
      console.error('Error fetching event data:', error);
    }
  };

  const fetchEventCoaches = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('event_coaches')
        .select('*')
        .eq('event_id', eventId)
        .order('coach_name');

      if (error) throw error;
      setCoaches(data || []);
    } catch (error) {
      console.error('Error fetching event coaches:', error);
      toast.error('Failed to load coaches');
    } finally {
      setLoading(false);
    }
  };

  const addCoach = () => {
    setCoaches([...coaches, {
      coach_name: '',
      college_id: null,
      college_name: '',
      division: 'D1',
      logo_url: null,
      external_url: null
    }]);
    setEditingIndex(coaches.length);
    setShowAddForm(true);
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setShowAddForm(false);
    // Remove empty coach if user cancels
    const filteredCoaches = coaches.filter(coach => coach.coach_name.trim() !== '');
    setCoaches(filteredCoaches);
  };

  const updateCoach = (index: number, field: keyof EventCoach, value: any) => {
    const updatedCoaches = [...coaches];
    updatedCoaches[index] = { ...updatedCoaches[index], [field]: value };
    setCoaches(updatedCoaches);
  };

  const removeCoach = (index: number) => {
    const updatedCoaches = coaches.filter((_, i) => i !== index);
    setCoaches(updatedCoaches);
  };

  const toggleCoachVisibility = async (index: number) => {
    const coach = coaches[index];
    const newVisibility = !coach.hide_from_public;
    
    // Update local state immediately
    const updatedCoaches = [...coaches];
    updatedCoaches[index] = { ...updatedCoaches[index], hide_from_public: newVisibility };
    setCoaches(updatedCoaches);
    
    // If coach has an ID, update in database immediately
    if (coach.id) {
      try {
        const { error } = await supabase
          .from('event_coaches')
          .update({ hide_from_public: newVisibility })
          .eq('id', coach.id);
        
        if (error) throw error;
        toast.success(newVisibility ? 'Coach hidden from public page' : 'Coach visible on public page');
      } catch (error) {
        console.error('Error updating coach visibility:', error);
        toast.error('Failed to update visibility');
        // Revert on error
        updatedCoaches[index] = { ...updatedCoaches[index], hide_from_public: !newVisibility };
        setCoaches(updatedCoaches);
      }
    }
  };

  const handleLogoUpload = async (index: number, file: File) => {
    try {
      // Sanitize filename to remove special characters and spaces
      const fileExtension = file.name.split('.').pop() || 'png';
      const sanitizedName = `coach_logo_${Date.now()}.${fileExtension}`;
      const fileName = `${eventId}/${sanitizedName}`;
      
      const { data, error } = await supabase.storage
        .from('coach_logos')
        .upload(fileName, file, {
          upsert: true
        });

      if (error) throw error;

      const { data: urlData } = supabase.storage
        .from('coach_logos')
        .getPublicUrl(data.path);

      updateCoach(index, 'logo_url', urlData.publicUrl);
      toast.success('Logo uploaded successfully');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    }
  };

  const saveCoaches = async () => {
    try {
      setSaving(true);

      console.log('🎯 ===== SAVE COACHES PROCESS STARTED =====');
      console.log('🎯 Event ID:', eventId);
      console.log('🎯 Current coaches state:', JSON.stringify(coaches, null, 2));
      console.log('🎯 Number of coaches in state:', coaches.length);
      console.log('🎯 Saving coaches_external_url:', coachesExternalUrl);
      console.log('🎯 Saving coach_attendance_fee:', coachAttendanceFee);

      // Convert fees to cents
      const feeInCents = Math.round(parseFloat(coachAttendanceFee || '0') * 100);
      const ncaaD1FirstCents = Math.round(parseFloat(ncaaD1FirstFee || '0') * 100);
      const ncaaD1AdditionalCents = Math.round(parseFloat(ncaaD1AdditionalFee || '0') * 100);
      const ncaaOtherFirstCents = Math.round(parseFloat(ncaaOtherFirstFee || '0') * 100);
      const ncaaOtherAdditionalCents = Math.round(parseFloat(ncaaOtherAdditionalFee || '0') * 100);
      const hospitalityDaysArray = ncaaHospitalityDays.split('\n').map(d => d.trim()).filter(d => d);

      console.log('🎯 NCAA Live values being saved:', {
        isNcaaLiveEvent,
        ncaaD1FirstCents,
        ncaaD1AdditionalCents,
        ncaaOtherFirstCents,
        ncaaOtherAdditionalCents,
        hospitalityDaysArray,
        couponCodes: couponCodes.length
      });

      // DEBUG: Check auth session before update
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      console.log('🎯 AUTH DEBUG - Session check:', {
        hasSession: !!sessionData?.session,
        userId: sessionData?.session?.user?.id,
        userEmail: sessionData?.session?.user?.email,
        sessionError: sessionError?.message
      });

      if (!sessionData?.session) {
        toast.error('Not authenticated. Please log in again.');
        throw new Error('No active session');
      }

      // Update the event's coach settings (no longer storing coupon in events table)
      const { data: updateData, error: updateError } = await supabase
        .from('events')
        .update({ 
          coaches_external_url: coachesExternalUrl || null,
          coach_attendance_fee: feeInCents,
          coach_coupon_code: coachCouponCode || null,
          is_ncaa_live_event: isNcaaLiveEvent,
          ncaa_d1_first_coach_fee: ncaaD1FirstCents,
          ncaa_d1_additional_coach_fee: ncaaD1AdditionalCents,
          ncaa_other_first_coach_fee: ncaaOtherFirstCents,
          ncaa_other_additional_coach_fee: ncaaOtherAdditionalCents,
          ncaa_hospitality_days: hospitalityDaysArray,
          coach_registration_template_id: registrationTemplateId,
          coach_registration_instructions: coachRegistrationInstructions || null,
          coach_notification_email: coachNotificationEmail || null
        })
        .eq('id', eventId)
        .select('coaches_external_url, coach_attendance_fee, coach_coupon_code, is_ncaa_live_event, ncaa_d1_first_coach_fee, ncaa_d1_additional_coach_fee, ncaa_other_first_coach_fee, ncaa_other_additional_coach_fee, ncaa_hospitality_days, coach_registration_template_id, coach_registration_instructions, coach_notification_email')
        .single();

      // Save coupon codes to the new table
      // Delete existing coupons for this event
      await supabase
        .from('event_coupon_codes')
        .delete()
        .eq('event_id', eventId);
      
      // Insert new coupon codes
      if (couponCodes.length > 0) {
        const couponInserts = couponCodes
          .filter(c => c.code.trim())
          .map(c => ({
            event_id: eventId,
            code: c.code.toUpperCase().trim(),
            discount_type: c.discount_type,
            discount_value: c.discount_type === 'fixed' 
              ? Math.round(parseFloat(c.discount_value || '0') * 100)
              : parseFloat(c.discount_value || '0'),
            is_active: c.is_active
          }));
        
        if (couponInserts.length > 0) {
          const { error: couponError } = await supabase
            .from('event_coupon_codes')
            .insert(couponInserts);
          
          if (couponError) {
            console.error('Error saving coupon codes:', couponError);
            toast.error('Failed to save coupon codes');
          }
        }
      }

      if (updateError) {
        console.error('🎯 Error updating coach settings:', updateError);
        // Check for RLS permission error
        if (updateError.code === 'PGRST116' || updateError.message?.includes('no rows')) {
          toast.error('Permission denied: You may not have admin access. Please log in again.');
        } else if (updateError.code === '42501' || updateError.message?.includes('permission denied')) {
          toast.error('Permission denied: Admin access required to update event settings.');
        } else {
          toast.error('Failed to save NCAA Live settings: ' + updateError.message);
        }
        throw updateError;
      }

      // Check if update actually returned data (RLS might silently block)
      if (!updateData) {
        console.error('🎯 Update returned no data - possible RLS policy block');
        toast.error('Failed to save: Permission denied or event not found. Please ensure you are logged in as admin.');
        throw new Error('Update returned no data');
      }

      console.log('🎯 Successfully saved coach settings - FULL RESPONSE:', updateData);
      
      // Verify the save worked by comparing values
      const expectedD1FirstFee = ncaaD1FirstCents;
      const actualD1FirstFee = updateData.ncaa_d1_first_coach_fee;
      
      if (actualD1FirstFee !== expectedD1FirstFee) {
        console.warn('🎯 WARNING: Saved value mismatch!', {
          expected: expectedD1FirstFee,
          actual: actualD1FirstFee
        });
      }
      
      console.log('🎯 Verified saved values:', {
        ncaa_d1_first_coach_fee: updateData.ncaa_d1_first_coach_fee,
        ncaa_d1_additional_coach_fee: updateData.ncaa_d1_additional_coach_fee,
        ncaa_other_first_coach_fee: updateData.ncaa_other_first_coach_fee,
        ncaa_other_additional_coach_fee: updateData.ncaa_other_additional_coach_fee,
        ncaa_hospitality_days: updateData.ncaa_hospitality_days
      });

      // Delete existing coaches for this event
      const { error: deleteError } = await supabase
        .from('event_coaches')
        .delete()
        .eq('event_id', eventId);

      if (deleteError) throw deleteError;

      console.log('🗑️ Deleted existing coaches');

      // Insert new coaches
      console.log('🏈 ===== COACH INSERTION PROCESS =====');
      console.log('🏈 Total coaches in state:', coaches.length);
      
      if (coaches.length > 0) {
        console.log('🏈 Processing coaches for database insert...');
        
        // Log each coach before filtering
        coaches.forEach((coach, index) => {
          console.log(`🏈 Coach ${index + 1}:`, {
            coach_name: coach.coach_name,
            college_name: coach.college_name,
            college_id: coach.college_id,
            division: coach.division,
            logo_url: coach.logo_url,
            external_url: coach.external_url
          });
        });
        
        // Filter out completely empty coaches (those with no college name)
        const coachesToInsert = coaches
          .filter(coach => {
            const hasCollegeName = coach.college_name?.trim();
            console.log(`🏈 Filter check - College: "${coach.college_name}", Has name: ${!!hasCollegeName}, Will insert: ${hasCollegeName}`);
            return hasCollegeName;
          })
          .map(coach => ({
            event_id: eventId,
            coach_name: coach.coach_name?.trim() || null,
            college_id: coach.college_id,
            college_name: coach.college_name?.trim(),
            division: coach.division,
            logo_url: coach.logo_url,
            coach_email: coach.coach_email || null,
            coach_phone: coach.coach_phone || null,
            payment_status: coach.payment_status || 'pending',
            hide_from_public: coach.hide_from_public || false,
            metadata: coach.metadata || null
          }));

        console.log('🏈 Filtered coaches to insert:', coachesToInsert);

        if (coachesToInsert.length > 0) {
          const coachesWithUrl = coachesToInsert.map(coach => ({
            ...coach,
            external_url: coaches.find(c => c.college_name === coach.college_name)?.external_url || null
          }));
          
          console.log('🏈 Final coaches being saved to DB:', coachesWithUrl);
          console.log('🏈 Logo URLs:', coachesWithUrl.map(c => ({ name: c.college_name, logo: c.logo_url })));
          
          const { data: insertData, error: insertError } = await supabase
            .from('event_coaches')
            .insert(coachesWithUrl)
            .select();

          if (insertError) {
            console.error('🚨 Insert error details:', insertError);
            throw insertError;
          }
          
          console.log('✅ Successfully inserted coaches:', insertData);
        } else {
          console.log('⚠️ No valid coaches to insert (all filtered out)');
        }
      } else {
        console.log('⚠️ No coaches in state to save');
      }

      toast.success('Coaches saved successfully');
      fetchEventCoaches(); // Refresh the list
    } catch (error) {
      console.error('🚨 Error saving coaches:', error);
      toast.error('Failed to save coaches');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">Loading coaches...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-8">
      {/* Sticky Save Bar at Top */}
      <div className="sticky top-0 z-10 bg-primary/10 border border-primary/30 rounded-lg p-4 flex items-center justify-between shadow-sm">
        <div>
          <h3 className="font-semibold text-lg">Coach Settings</h3>
          <p className="text-sm text-muted-foreground">
            Configure attendance fees, NCAA Live settings, and manage coaches
          </p>
        </div>
        <Button 
          onClick={saveCoaches} 
          disabled={saving} 
          size="lg" 
          className="font-semibold"
        >
          {saving ? (
            <>
              <span className="animate-spin mr-2">⏳</span> Saving...
            </>
          ) : (
            <>
              💾 Save All Settings
            </>
          )}
        </Button>
      </div>

      {/* Section 1: Coach Attendance Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            <span>Coach Attendance Settings</span>
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Configure attendance fee and tracking for college coaches
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Attendance Fee */}
          <div className="space-y-2">
            <Label htmlFor="coach-attendance-fee" className="text-base font-medium">
              Attendance Fee
            </Label>
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold">$</span>
              <Input
                id="coach-attendance-fee"
                type="number"
                min="0"
                step="0.01"
                value={coachAttendanceFee}
                onChange={(e) => setCoachAttendanceFee(e.target.value)}
                placeholder="0.00"
                className="w-32"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Set to 0 for free attendance. Coaches will be charged via Stripe if fee is set.
            </p>
          </div>

          {/* Coupon Code */}
          <div className="space-y-2">
            <Label htmlFor="coach-coupon-code" className="text-base font-medium">
              Coupon Code (Optional)
            </Label>
            <Input
              id="coach-coupon-code"
              type="text"
              value={coachCouponCode}
              onChange={(e) => setCoachCouponCode(e.target.value.toUpperCase())}
              placeholder="e.g., COACH2024"
              className="w-48"
            />
            <p className="text-xs text-muted-foreground">
              Coaches who enter this code will get free registration.
            </p>
          </div>

          {/* Registration Email Template */}
          <div className="space-y-2">
            <Label htmlFor="coach-registration-template" className="text-base font-medium">
              Registration Confirmation Email Template
            </Label>
            <Select
              value={registrationTemplateId || 'none'}
              onValueChange={(value) => setRegistrationTemplateId(value === 'none' ? null : value)}
            >
              <SelectTrigger className="w-80">
                <SelectValue placeholder="Select email template..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Use default template</SelectItem>
                {availableTemplates.map((template) => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name} ({template.category})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Select the email template to send when a coach registers for this event.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Section 2: NCAA Live Event Settings */}
      <Card className={isNcaaLiveEvent ? 'border-primary' : ''}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5" />
                <span>NCAA Live Event</span>
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Enable multi-tier pricing for NCAA Live Period events
              </p>
            </div>
            <Switch
              checked={isNcaaLiveEvent}
              onCheckedChange={setIsNcaaLiveEvent}
            />
          </div>
        </CardHeader>
        {isNcaaLiveEvent && (
          <CardContent className="space-y-6">
            {/* Division One Pricing */}
            <div className="space-y-4">
              <h4 className="font-semibold text-primary">Division One Pricing</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>1st Coach Price</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={ncaaD1FirstFee}
                      onChange={(e) => setNcaaD1FirstFee(e.target.value)}
                      placeholder="395.00"
                      className="w-32"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Registration fee for first D1 coach</p>
                </div>
                <div className="space-y-2">
                  <Label>Additional Coach Price</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={ncaaD1AdditionalFee}
                      onChange={(e) => setNcaaD1AdditionalFee(e.target.value)}
                      placeholder="100.00"
                      className="w-32"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Per additional D1 coach</p>
                </div>
              </div>
            </div>

            {/* Other Divisions Pricing */}
            <div className="space-y-4">
              <h4 className="font-semibold text-primary">All Other Divisions (D2, D3, NAIA, JUCO)</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>1st Coach Price</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={ncaaOtherFirstFee}
                      onChange={(e) => setNcaaOtherFirstFee(e.target.value)}
                      placeholder="125.00"
                      className="w-32"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Registration fee for first coach</p>
                </div>
                <div className="space-y-2">
                  <Label>Additional Coach Price</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={ncaaOtherAdditionalFee}
                      onChange={(e) => setNcaaOtherAdditionalFee(e.target.value)}
                      placeholder="50.00"
                      className="w-32"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Per additional coach</p>
                </div>
              </div>
            </div>

            {/* Coupon Code Settings */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-primary">Coupon Codes (Optional)</h4>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCouponCodes([...couponCodes, { code: '', discount_type: 'percentage', discount_value: '100', is_active: true }])}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Coupon
                </Button>
              </div>
              
              {couponCodes.length === 0 ? (
                <p className="text-sm text-muted-foreground">No coupon codes configured. Click "Add Coupon" to create one.</p>
              ) : (
                <div className="space-y-3">
                  {couponCodes.map((coupon, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30">
                      <div className="flex-1 space-y-1">
                        <Label className="text-xs">Coupon Code</Label>
                        <Input
                          type="text"
                          value={coupon.code}
                          onChange={(e) => {
                            const updated = [...couponCodes];
                            updated[index].code = e.target.value.toUpperCase();
                            setCouponCodes(updated);
                          }}
                          placeholder="e.g., MIKE2025"
                          className="w-full"
                        />
                      </div>
                      <div className="w-40 space-y-1">
                        <Label className="text-xs">Discount Type</Label>
                        <Select 
                          value={coupon.discount_type} 
                          onValueChange={(v) => {
                            const updated = [...couponCodes];
                            updated[index].discount_type = v as 'percentage' | 'fixed';
                            setCouponCodes(updated);
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percentage">Percentage (%)</SelectItem>
                            <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-28 space-y-1">
                        <Label className="text-xs">Value</Label>
                        <div className="flex items-center gap-1">
                          <span className="text-sm font-medium">{coupon.discount_type === 'percentage' ? '%' : '$'}</span>
                          <Input
                            type="number"
                            min="0"
                            step={coupon.discount_type === 'percentage' ? '1' : '0.01'}
                            max={coupon.discount_type === 'percentage' ? '100' : undefined}
                            value={coupon.discount_value}
                            onChange={(e) => {
                              const updated = [...couponCodes];
                              updated[index].discount_value = e.target.value;
                              setCouponCodes(updated);
                            }}
                            placeholder={coupon.discount_type === 'percentage' ? '100' : '50'}
                            className="w-20"
                          />
                        </div>
                      </div>
                      <div className="pt-5">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:bg-destructive/10"
                          onClick={() => {
                            const updated = couponCodes.filter((_, i) => i !== index);
                            setCouponCodes(updated);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Each coupon can have its own discount type and value. 100% = free registration.
              </p>
            </div>

            {/* Hospitality Days */}
            <div className="space-y-2">
              <Label>Hospitality Room Days</Label>
              <Textarea
                value={ncaaHospitalityDays}
                onChange={(e) => setNcaaHospitalityDays(e.target.value)}
                placeholder="Enter each day on a new line, e.g.:&#10;Friday July 19&#10;Saturday July 20&#10;Sunday July 21"
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                These options will appear as checkboxes for coaches to indicate attendance days
              </p>
            </div>

            {/* Registration Form Instructions */}
            <div className="space-y-2">
              <Label>Registration Form Instructions</Label>
              <Textarea
                value={coachRegistrationInstructions}
                onChange={(e) => setCoachRegistrationInstructions(e.target.value)}
                placeholder="Enter instructions to display at the top of the coach registration form...&#10;&#10;Example:&#10;Coaches-&#10;&#10;Thank you for your interest in attending RecruitLook Hoops' NCAA Live Period in CHICAGO on July 16-19 weekend. Here are a few things to know.&#10;&#10;1.) The first attending coach from a school must purchase a packet...&#10;2.) College coaches must check in and pick up packets at...&#10;3.) We will have a hospitality room at..."
                rows={8}
              />
              <p className="text-xs text-muted-foreground">
                This text will display at the top of the registration form to provide instructions to coaches before they fill out the form
              </p>
            </div>
            {/* Registration Confirmation Template */}
            <div className="space-y-2">
              <Label>Coach Registration Confirmation Template</Label>
              <Select 
                value={registrationTemplateId || 'none'} 
                onValueChange={(v) => setRegistrationTemplateId(v === 'none' ? null : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a template for registration confirmations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No template (default system email)</SelectItem>
                  {availableTemplates.filter(t => t.id && t.id.trim() !== '').map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name} ({template.category})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Choose which email template to send when coaches register for this event
              </p>
            </div>

            {/* Notification Email */}
            <div className="space-y-2">
              <Label>Coach Registration Notification Emails</Label>
              <Input
                type="text"
                value={coachNotificationEmail}
                onChange={(e) => setCoachNotificationEmail(e.target.value)}
                placeholder="admin@example.com, manager@example.com"
              />
              <p className="text-xs text-muted-foreground">
                Email addresses to notify when coaches register for this event (comma-separated for multiple)
              </p>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Section 3: External Registration URL (Legacy) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span>🔗 External Registration URL (Optional)</span>
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Alternative: Provide an external link for coach registration (overrides built-in registration)
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="coaches-external-url" className="text-base">
              Registration URL
            </Label>
            <Input
              id="coaches-external-url"
              value={coachesExternalUrl}
              onChange={(e) => setCoachesExternalUrl(e.target.value)}
              placeholder="https://example.com/coach-registration"
              className="mt-2 font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground mt-2">
              ⚠️ If set, coaches will be redirected to this URL instead of using the built-in registration.
            </p>
            {coachesExternalUrl && !coachesExternalUrl.startsWith('http://') && !coachesExternalUrl.startsWith('https://') && (
              <p className="text-xs text-destructive mt-1 font-semibold">
                ⚠️ URL must start with http:// or https://
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Section 3: Registered Coaches */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span>👥 Registered Coaches</span>
            {coaches.length > 0 && (
              <Badge variant="secondary">{coaches.length} registered</Badge>
            )}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Coaches who have registered to attend this event
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {coaches.length === 0 && !showAddForm ? (
            <div className="text-center py-8 border-2 border-dashed rounded-lg">
              <p className="text-muted-foreground mb-4">No coaches registered yet</p>
              <Button onClick={addCoach} variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Add Coach Manually
              </Button>
            </div>
          ) : (
            <>
              {/* Coaches Table */}
              {coaches.filter((c, i) => i !== editingIndex).length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[80px]">Visible</TableHead>
                        <TableHead>Coach Name</TableHead>
                        <TableHead>College</TableHead>
                        <TableHead>Division</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Additional Coaches</TableHead>
                        <TableHead>Hospitality Days</TableHead>
                        <TableHead>Payment</TableHead>
                        <TableHead className="w-[80px]">Logo</TableHead>
                        <TableHead className="w-[100px] text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {coaches.map((coach, index) => {
                        if (index === editingIndex) return null;
                        
                        return (
                          <TableRow key={index} className={coach.hide_from_public ? 'opacity-50' : ''}>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleCoachVisibility(index)}
                                title={coach.hide_from_public ? 'Hidden from public - click to show' : 'Visible on public page - click to hide'}
                              >
                                {coach.hide_from_public ? (
                                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                  <Eye className="h-4 w-4 text-green-600" />
                                )}
                              </Button>
                            </TableCell>
                            <TableCell className="font-medium">{coach.coach_name || '-'}</TableCell>
                            <TableCell>{coach.college_name || '-'}</TableCell>
                            <TableCell>{coach.division}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{coach.coach_email || '-'}</TableCell>
                            <TableCell className="text-sm">
                              {coach.metadata?.additional_coaches && coach.metadata.additional_coaches.length > 0 ? (
                                <div className="space-y-0.5">
                                  {coach.metadata.additional_coaches.map((name, i) => (
                                    <div key={i} className="text-xs">{name}</div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm">
                              {coach.metadata?.hospitality_days && coach.metadata.hospitality_days.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {coach.metadata.hospitality_days.map((day, i) => (
                                    <Badge key={i} variant="outline" className="text-xs">{day}</Badge>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge 
                                variant={coach.payment_status === 'paid' ? 'default' : 
                                        coach.payment_status === 'free' ? 'secondary' : 'outline'}
                              >
                                {coach.payment_status || 'pending'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {coach.logo_url && (
                                <img
                                  src={coach.logo_url}
                                  alt="College logo"
                                  className="h-8 w-8 object-contain rounded"
                                />
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setEditingIndex(index)}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeCoach(index)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Edit/Add Form */}
              {editingIndex !== null && (
                <div className="border rounded-lg p-4 space-y-4 bg-muted/50">
                  <div className="flex justify-between items-center">
                    <h3 className="font-medium">
                      {coaches[editingIndex]?.id ? 'Edit Coach' : 'Add New Coach'}
                    </h3>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={cancelEdit}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Cancel
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => {
                          // Validate that at least college name is filled
                          if (coaches[editingIndex]?.college_name?.trim()) {
                            setEditingIndex(null);
                            setShowAddForm(false);
                            toast.success('Coach details saved. Click "Save Coaches & External URL" to save to database.');
                          } else {
                            toast.error('College name is required');
                          }
                        }}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Done
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor={`coach-name-${editingIndex}`}>Coach Name (Optional)</Label>
                      <Input
                        id={`coach-name-${editingIndex}`}
                        value={coaches[editingIndex]?.coach_name || ''}
                        onChange={(e) => updateCoach(editingIndex, 'coach_name', e.target.value)}
                        placeholder="Enter coach name (optional)"
                      />
                    </div>

                    <div>
                      <Label htmlFor={`college-${editingIndex}`}>College *</Label>
                      <Input
                        id={`college-${editingIndex}`}
                        value={coaches[editingIndex]?.college_name || ''}
                        onChange={(e) => updateCoach(editingIndex, 'college_name', e.target.value)}
                        placeholder="Enter college name"
                        className="border-primary/30"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Required - this will be displayed on the event page</p>
                    </div>

                    <div>
                      <Label htmlFor={`division-${editingIndex}`}>Division</Label>
                      <Select
                        value={coaches[editingIndex]?.division || 'D1'}
                        onValueChange={(value) => updateCoach(editingIndex, 'division', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {divisions.map((division) => (
                            <SelectItem key={division} value={division}>
                              {division}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor={`payment-status-${editingIndex}`}>Payment Status</Label>
                      <Select
                        value={coaches[editingIndex]?.payment_status || 'pending'}
                        onValueChange={(value) => updateCoach(editingIndex, 'payment_status', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="paid">Paid</SelectItem>
                          <SelectItem value="free">Free</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor={`logo-${editingIndex}`}>College Logo</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id={`logo-${editingIndex}`}
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              handleLogoUpload(editingIndex, file);
                            }
                          }}
                          className="flex-1"
                        />
                        {coaches[editingIndex]?.logo_url && (
                          <div className="flex items-center gap-2">
                            <img
                              src={coaches[editingIndex].logo_url!}
                              alt="College logo"
                              className="h-10 w-10 object-contain rounded border"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => updateCoach(editingIndex, 'logo_url', null)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {coaches[editingIndex]?.logo_url 
                          ? '✓ Logo uploaded' 
                          : '⚠️ No logo uploaded - college initials will be shown'}
                      </p>
                    </div>

                    <div className="md:col-span-2">
                      <Label htmlFor={`external-url-${editingIndex}`}>Coach Profile URL (Optional)</Label>
                      <Input
                        id={`external-url-${editingIndex}`}
                        value={coaches[editingIndex]?.external_url || ''}
                        onChange={(e) => updateCoach(editingIndex, 'external_url', e.target.value)}
                        placeholder="https://example.com/coach-profile"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        If provided, clicking this coach on the event page will open this URL
                      </p>
                    </div>

                    {/* Additional Coaches */}
                    <div className="md:col-span-2">
                      <Label htmlFor={`additional-coaches-${editingIndex}`}>Additional Coaches Attending</Label>
                      <Textarea
                        id={`additional-coaches-${editingIndex}`}
                        value={coaches[editingIndex]?.metadata?.additional_coaches?.join('\n') || ''}
                        onChange={(e) => {
                          const names = e.target.value.split('\n').filter(n => n.trim());
                          const currentMetadata = coaches[editingIndex]?.metadata || {};
                          updateCoach(editingIndex, 'metadata', {
                            ...currentMetadata,
                            additional_coaches: names,
                            additional_coach_qty: names.length
                          });
                        }}
                        placeholder="Enter one coach name per line"
                        rows={3}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Enter additional coaches attending with this registration (one per line)
                      </p>
                    </div>

                    {/* Hospitality Days */}
                    <div className="md:col-span-2">
                      <Label htmlFor={`hospitality-days-${editingIndex}`}>Hospitality Room Access</Label>
                      <Input
                        id={`hospitality-days-${editingIndex}`}
                        value={coaches[editingIndex]?.metadata?.hospitality_days?.join(', ') || ''}
                        onChange={(e) => {
                          const days = e.target.value.split(',').map(d => d.trim()).filter(d => d);
                          const currentMetadata = coaches[editingIndex]?.metadata || {};
                          updateCoach(editingIndex, 'metadata', {
                            ...currentMetadata,
                            hospitality_days: days
                          });
                        }}
                        placeholder="e.g., Friday, Saturday, All Days"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Days they have access to the hospitality room (comma-separated)
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {editingIndex === null && (
                <Button onClick={addCoach} variant="outline" className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Another Coach
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Save Button - Make it very prominent */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-lg mb-1">Ready to save?</h3>
              <p className="text-sm text-muted-foreground">
                Click the button below to save attendance fee, Google Sheets settings, and all coaches.
              </p>
            </div>
            <Button 
              onClick={saveCoaches} 
              disabled={saving} 
              size="lg" 
              className="font-semibold ml-4"
            >
              {saving ? (
                <>
                  <span className="animate-spin mr-2">⏳</span> Saving...
                </>
              ) : (
                <>
                  💾 Save All Settings
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CoachesTab;